import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Service discovery and health console (part 34).
 *
 * The page shows the target registry, the result of real HTTP probes, the drain window and the
 * derived weighted routing table. It is not the load balancer: the API owns the state machine and
 * the table; this screen lets an operator register an endpoint, probe it, drain it and watch the
 * weight move. A drain keeps the target's configured weight until it is completed, at which point
 * the weight reaches zero in one step. The page never suggests otherwise.
 *
 * Not routed from App.tsx in this part; the API mount is also intentionally unmounted. Both are
 * one-line wiring changes reviewed with the first operator who needs the controller.
 */

type TargetState = 'healthy' | 'draining' | 'down'
type ProbeOutcome = 'healthy' | 'slow' | 'unhealthy'
type DrainState = 'active' | 'completed' | 'cancelled'

interface ServiceTarget {
  id: string
  name: string
  endpoint: string
  weight: number
  state: TargetState
  failure_threshold: number
  promotion_checks: number
  drain_window_seconds: number
  consecutive_failures: number
  consecutive_successes: number
  retired: boolean
  admitted_at?: string
  down_at?: string
  last_probe_at?: string
  last_status_code: number
  last_latency_ms: number
  last_error: string
  created_at: string
  updated_at: string
}

interface Drain {
  id: string
  target_id: string
  target_name?: string
  state: DrainState
  reason: string
  requested_by?: string
  weight_held: number
  started_at: string
  deadline_at: string
  completed_at?: string
  cancelled_at?: string
}

interface TargetView extends ServiceTarget {
  ready: boolean
  effective_weight: number
  percent: number
  active_drain?: Drain
  drain_progress: number
  drain_remaining_seconds: number
}

interface HealthCheck {
  id: string
  target_id: string
  target_name?: string
  outcome: ProbeOutcome
  status_code: number
  latency_ms: number
  error: string
  state_after: TargetState
  consecutive_failures: number
  consecutive_successes: number
  source: string
  checked_at: string
}

interface RoutingEntry {
  target_id: string
  name: string
  endpoint: string
  state: TargetState
  weight: number
  effective_weight: number
  percent: number
  draining: boolean
  drain_deadline?: string
  drain_remaining_seconds: number
}

interface RoutingTable {
  entries: RoutingEntry[]
  total_weight: number
  excluded: number
  generated_at: string
  note: string
}

interface HealthSummary {
  targets: number
  healthy: number
  draining: number
  down: number
  retired: number
  in_rotation: number
  total_weight: number
  checks: number
  healthy_checks: number
  slow_checks: number
  failed_checks: number
  avg_latency_ms: number
  last_probe_at?: string
}

interface DiscoveryPolicy {
  probe_timeout_ms: number
  slow_threshold_ms: number
  default_failure_threshold: number
  default_promotion_checks: number
  default_drain_window_seconds: number
}

interface DiscoverySnapshot {
  targets: TargetView[]
  routing: RoutingTable
  health: HealthSummary
  drains: Drain[]
  checks: HealthCheck[]
  server_at: string
  note: string
  policy: DiscoveryPolicy
}

interface ProbeResult {
  target: ServiceTarget
  check: HealthCheck
  change: {
    before: TargetState
    after: TargetState
    changed: boolean
    promoted: boolean
    failed_out: boolean
    reason: string
  }
  probed_at: string
  note: string
}

interface DrainResult {
  target: ServiceTarget
  drain: Drain
  weight_held: number
  weight_now?: number
  note: string
}

interface SwitchPlan {
  from_target_id: string
  from_name: string
  to_target_id: string
  to_name: string
  checks_passed: number
  required_checks: number
  drain: Drain
  effective_at: string
  overlap_until: string
  note: string
}

const STATE_LABEL: Record<TargetState, string> = {
  healthy: 'พร้อมใช้งาน',
  draining: 'กำลังระบาย',
  down: 'นอกเส้นทาง',
}

const STATE_TONE: Record<TargetState, string> = {
  healthy: 'sev-low',
  draining: 'sev-medium',
  down: 'sev-critical',
}

const OUTCOME_LABEL: Record<ProbeOutcome, string> = {
  healthy: 'ผ่าน',
  slow: 'ผ่านแต่ช้า',
  unhealthy: 'ไม่ผ่าน',
}

const OUTCOME_TONE: Record<ProbeOutcome, string> = {
  healthy: 'sev-low',
  slow: 'sev-medium',
  unhealthy: 'sev-critical',
}

const STATE_KEY = ['discovery-state']

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function secondsLabel(seconds: number): string {
  if (seconds <= 0) return 'ครบกำหนดแล้ว'
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60)
    const rest = seconds % 60
    return rest === 0 ? `${minutes} นาที` : `${minutes} นาที ${rest} วินาที`
  }
  return `${seconds} วินาที`
}

function windowLabel(seconds: number): string {
  if (seconds >= 3600 && seconds % 3600 === 0) return `${seconds / 3600} ชั่วโมง`
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} นาที`
  return `${seconds} วินาที`
}

export function Discovery() {
  const qc = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)
  const [confirmComplete, setConfirmComplete] = useState<string | null>(null)

  const [form, setForm] = useState({
    name: '',
    endpoint: '',
    weight: '100',
    failure_threshold: '',
    promotion_checks: '',
    drain_window_seconds: '',
  })

  const [switchForm, setSwitchForm] = useState({ from: '', to: '', reason: '' })

  const state = useQuery({
    queryKey: STATE_KEY,
    queryFn: () => api<DiscoverySnapshot>('/v1/discovery/state'),
    refetchInterval: 10_000,
    staleTime: 4_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: STATE_KEY })
  }

  const register = useMutation({
    mutationFn: () =>
      apiWrite<{ target: ServiceTarget; note: string }>('/v1/discovery/targets', 'POST', {
        name: form.name.trim(),
        endpoint: form.endpoint.trim(),
        weight: Number(form.weight) || 0,
        failure_threshold: Number(form.failure_threshold) || 0,
        promotion_checks: Number(form.promotion_checks) || 0,
        drain_window_seconds: Number(form.drain_window_seconds) || 0,
      }),
    onSuccess: (result) => {
      setNotice(
        `ลงทะเบียน ${result.target.name} แล้ว · สถานะเริ่มต้นคือนอกเส้นทาง ` +
          `ต้องผ่านการตรวจต่อเนื่อง ${result.target.promotion_checks} ครั้งก่อนรับงาน`,
      )
      setForm({
        name: '',
        endpoint: '',
        weight: '100',
        failure_threshold: '',
        promotion_checks: '',
        drain_window_seconds: '',
      })
      refresh()
    },
  })

  const probe = useMutation({
    mutationFn: (targetID: string) =>
      apiWrite<ProbeResult>(`/v1/discovery/targets/${targetID}/probe`, 'POST'),
    onSuccess: (result) => {
      setNotice(
        `ตรวจ ${result.target.name} · ${OUTCOME_LABEL[result.check.outcome]}` +
          (result.check.status_code > 0 ? ` (HTTP ${result.check.status_code})` : ' (ไม่มีการตอบกลับ)') +
          ` · สถานะ ${STATE_LABEL[result.target.state]} · ${result.note}`,
      )
      refresh()
    },
  })

  const startDrain = useMutation({
    mutationFn: (targetID: string) =>
      apiWrite<DrainResult>(`/v1/discovery/targets/${targetID}/drain`, 'POST', {
        reason: 'ระบายจากหน้าควบคุม',
      }),
    onSuccess: (result) => {
      setNotice(
        `เริ่ม drain ${result.target.name} · น้ำหนัก ${result.weight_held} ยังอยู่ในตารางเส้นทาง ` +
          `จนกว่าจะจบ drain ภายใน ${windowLabel(result.target.drain_window_seconds)}`,
      )
      setConfirmComplete(null)
      refresh()
    },
  })

  const completeDrain = useMutation({
    mutationFn: (targetID: string) =>
      apiWrite<DrainResult>(`/v1/discovery/targets/${targetID}/drain/complete`, 'POST'),
    onSuccess: (result) => {
      setNotice(
        `จบ drain ${result.target.name} แล้ว · น้ำหนักปัจจุบัน ${result.weight_now ?? 0} ` +
          'และถูกถอดออกจากตารางเส้นทางถาวร',
      )
      setConfirmComplete(null)
      refresh()
    },
  })

  const cancelDrain = useMutation({
    mutationFn: (targetID: string) =>
      apiWrite<DrainResult>(`/v1/discovery/targets/${targetID}/drain/cancel`, 'POST', {
        reason: 'ยกเลิกจากหน้าควบคุม',
      }),
    onSuccess: (result) => {
      setNotice(`ยกเลิก drain ${result.target.name} แล้ว · กลับเข้าตารางเส้นทางด้วยน้ำหนักเดิม`)
      setConfirmComplete(null)
      refresh()
    },
  })

  const switchTarget = useMutation({
    mutationFn: () =>
      apiWrite<SwitchPlan>('/v1/discovery/switch', 'POST', {
        from_target_id: switchForm.from,
        to_target_id: switchForm.to,
        reason: switchForm.reason.trim(),
      }),
    onSuccess: (plan) => {
      setNotice(
        `สลับจาก ${plan.from_name} ไป ${plan.to_name} แล้ว · ตรวจผ่าน ${plan.checks_passed}/` +
          `${plan.required_checks} ครั้ง · เป้าหมายเดิมอยู่ในช่วง overlap ถึง ` +
          `${thaiDateTime(plan.overlap_until)} และยังมีน้ำหนักอยู่`,
      )
      setSwitchForm({ from: '', to: '', reason: '' })
      refresh()
    },
  })

  const snapshot = state.data
  const targets = snapshot?.targets ?? []
  const routing = snapshot?.routing
  const health = snapshot?.health
  const policy = snapshot?.policy

  const drainable = useMemo(() => targets.filter((t) => t.state === 'healthy' && !t.retired), [targets])
  const ready = useMemo(() => targets.filter((t) => t.ready && !t.retired), [targets])

  const passRate = health && health.checks > 0
    ? (health.healthy_checks + health.slow_checks) / health.checks
    : 0

  return (
    <>
      <PageHead
        title="การค้นพบบริการและสุขภาพเป้าหมาย"
        sub="ลงทะเบียนปลายทาง ตรวจสุขภาพด้วย HTTP จริง และควบคุมน้ำหนักในตารางเส้นทาง · การถอด target ออกจากเส้นทางเกิดเมื่อน้ำหนักเป็นศูนย์หลังจบ drain เท่านั้น"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{number(health?.targets ?? 0)} target</span>
        <span className="chip">{number(health?.healthy ?? 0)} พร้อมใช้งาน</span>
        <span className="chip gold">{number(health?.draining ?? 0)} กำลังระบาย</span>
        <span className="chip">{number(health?.down ?? 0)} นอกเส้นทาง</span>
        <span className="chip">{number(health?.in_rotation ?? 0)} อยู่ในเส้นทาง</span>
        <span className="chip">น้ำหนักรวม {number(health?.total_weight ?? 0)}</span>
      </div>

      <div className="grid-4 stagger" style={{ marginBottom: 22 }}>
        <Stat
          label="เป้าหมายที่ลงทะเบียน"
          value={number(health?.targets ?? 0)}
          unit="รายการ"
          foot={`ถอดออกถาวร ${number(health?.retired ?? 0)} · นอกเส้นทาง ${number(health?.down ?? 0)}`}
        />
        <Stat
          label="อยู่ในตารางเส้นทาง"
          value={number(health?.in_rotation ?? 0)}
          unit="รายการ"
          foot={`น้ำหนักรวม ${number(health?.total_weight ?? 0)} · เป้าหมายที่ถูกตัดออก ${number(routing?.excluded ?? 0)}`}
        />
        <Stat
          label="ผลตรวจที่ผ่าน"
          value={`${Math.round(passRate * 100)}%`}
          foot={`${number(health?.healthy_checks ?? 0)} ผ่าน · ${number(health?.slow_checks ?? 0)} ช้า · ${number(health?.failed_checks ?? 0)} ไม่ผ่าน`}
        />
        <Stat
          label="เวลาตอบสนองเฉลี่ย"
          value={number(health?.avg_latency_ms ?? 0)}
          unit="ms"
          foot={
            health?.last_probe_at
              ? `ตรวจล่าสุด ${thaiDateTime(health.last_probe_at)}`
              : 'ยังไม่มีการตรวจ'
          }
        />
      </div>

      {notice ? (
        <div
          className="card card-pad"
          role="status"
          style={{ marginBottom: 16, borderColor: 'var(--color-hairline-strong)' }}
        >
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="card-label">เป้าหมายและสถานะสุขภาพ</span>
          <button
            className="btn btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={refresh}
            disabled={state.isFetching}
          >
            {state.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        </div>

        <div className="table-wrap">
          {state.isLoading ? (
            <div style={{ padding: 16 }}><Skel h={240} /></div>
          ) : state.isError ? (
            <div className="form-error" style={{ margin: 16 }}>
              {errorText(state.error, 'โหลดสถานะการค้นพบบริการไม่สำเร็จ')}
            </div>
          ) : targets.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty
                title="ยังไม่มีเป้าหมายที่ลงทะเบียน"
                body="เพิ่มปลายทางแรกจากแบบฟอร์มด้านล่าง · target จะอยู่นอกเส้นทางจนกว่าจะผ่านการตรวจต่อเนื่องครบตามเกณฑ์"
              />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th>เป้าหมาย</th>
                  <th style={{ width: 120 }}>สถานะ</th>
                  <th style={{ width: 130, textAlign: 'right' }}>น้ำหนัก</th>
                  <th style={{ width: 150 }}>ผลต่อเนื่อง</th>
                  <th style={{ width: 190 }}>ตรวจล่าสุด</th>
                  <th style={{ width: 250 }}>การทำงาน</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((target) => (
                  <tr key={target.id} style={{ cursor: 'default' }}>
                    <td>
                      <div style={{ fontSize: 12.5 }}>{target.name}</div>
                      <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                        {target.endpoint}
                      </div>
                      {target.active_drain ? (
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 4 }}>
                          drain ถึง {thaiDateTime(target.active_drain.deadline_at)} · เหลือ{' '}
                          {secondsLabel(target.drain_remaining_seconds)}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <span className={`sev ${STATE_TONE[target.state]}`}>
                        <i className="sev-dot" />
                        {STATE_LABEL[target.state]}
                      </span>
                      {target.retired ? (
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>ถอดออกถาวร</div>
                      ) : null}
                    </td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>
                      {target.percent}%
                      <div className="dim" style={{ fontSize: 10.5 }}>
                        น้ำหนัก {target.effective_weight}/{target.weight}
                      </div>
                    </td>
                    <td>
                      <div className="dim" style={{ fontSize: 11 }}>
                        ล้มเหลวติดกัน {target.consecutive_failures}/{target.failure_threshold}
                      </div>
                      <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
                        ผ่านติดกัน {target.consecutive_successes}/{target.promotion_checks}
                      </div>
                      <span className="bar" style={{ display: 'block', marginTop: 6 }}>
                        <i
                          style={{
                            width: `${Math.min(100, (target.consecutive_successes / Math.max(1, target.promotion_checks)) * 100)}%`,
                            background:
                              target.state === 'down' ? 'var(--color-crimson)' : 'var(--color-emerald)',
                          }}
                        />
                      </span>
                    </td>
                    <td className="dim" style={{ fontSize: 11 }}>
                      {target.last_probe_at ? thaiDateTime(target.last_probe_at) : 'ยังไม่ตรวจ'}
                      <div style={{ marginTop: 2 }}>
                        {target.last_status_code > 0 ? `HTTP ${target.last_status_code}` : 'ไม่มีการตอบกลับ'}
                        {target.last_latency_ms > 0 ? ` · ${target.last_latency_ms} ms` : ''}
                      </div>
                      {target.last_error ? (
                        <div style={{ color: 'var(--color-crimson)', marginTop: 2 }}>
                          {target.last_error.slice(0, 80)}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-sm"
                          disabled={probe.isPending}
                          onClick={() => probe.mutate(target.id)}
                        >
                          ตรวจเดี๋ยวนี้
                        </button>
                        {target.state === 'healthy' && !target.retired ? (
                          <button
                            className="btn btn-sm"
                            disabled={startDrain.isPending}
                            onClick={() => startDrain.mutate(target.id)}
                          >
                            เริ่ม drain
                          </button>
                        ) : null}
                        {target.state === 'draining' ? (
                          confirmComplete === target.id ? (
                            <>
                              <button
                                className="btn btn-sm btn-primary"
                                disabled={completeDrain.isPending}
                                style={{ color: 'var(--color-crimson)' }}
                                onClick={() => completeDrain.mutate(target.id)}
                              >
                                ยืนยันจบ drain
                              </button>
                              <button className="btn btn-sm btn-ghost" onClick={() => setConfirmComplete(null)}>
                                ยกเลิก
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="btn btn-sm"
                                onClick={() => setConfirmComplete(target.id)}
                              >
                                จบ drain
                              </button>
                              <button
                                className="btn btn-sm btn-ghost"
                                disabled={cancelDrain.isPending}
                                onClick={() => cancelDrain.mutate(target.id)}
                              >
                                ยกเลิก drain
                              </button>
                            </>
                          )
                        ) : null}
                      </div>
                      {confirmComplete === target.id ? (
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>
                          จบ drain จะถอด target ออกถาวรและน้ำหนักจะเป็นศูนย์
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {probe.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            {errorText(probe.error, 'ตรวจสุขภาพไม่สำเร็จ')}
          </div>
        ) : null}
        {startDrain.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            {errorText(startDrain.error, 'เริ่ม drain ไม่สำเร็จ')}
          </div>
        ) : null}
        {completeDrain.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            {errorText(completeDrain.error, 'จบ drain ไม่สำเร็จ')}
          </div>
        ) : null}
        {cancelDrain.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            {errorText(cancelDrain.error, 'ยกเลิก drain ไม่สำเร็จ')}
          </div>
        ) : null}
      </div>

      <div className="grid-2" style={{ marginBottom: 22 }}>
        <div className="card card-pad">
          <div className="card-label">ลงทะเบียนเป้าหมายใหม่</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            ปลายทางต้องเป็น URL แบบ http หรือ https ที่ไม่มีความลับฝังอยู่ · ค่าเกณฑ์ที่เว้นว่างจะใช้ค่าเริ่มต้น
            ของระบบ · target ใหม่จะอยู่นอกเส้นทางจนกว่าจะผ่านการตรวจต่อเนื่อง
          </p>
          <div className="field">
            <label htmlFor="discovery-name">ชื่อเป้าหมาย</label>
            <input
              id="discovery-name"
              className="input"
              placeholder="payments-api"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="discovery-endpoint">ปลายทางสุขภาพ</label>
            <input
              id="discovery-endpoint"
              className="input"
              placeholder="http://127.0.0.1:8080/healthz"
              value={form.endpoint}
              onChange={(event) => setForm({ ...form, endpoint: event.target.value })}
            />
          </div>
          <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="discovery-weight">น้ำหนัก</label>
              <input
                id="discovery-weight"
                className="input"
                inputMode="numeric"
                value={form.weight}
                onChange={(event) => setForm({ ...form, weight: event.target.value })}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="discovery-fail">เกณฑ์ตัดออก</label>
              <input
                id="discovery-fail"
                className="input"
                inputMode="numeric"
                placeholder={String(policy?.default_failure_threshold ?? 3)}
                value={form.failure_threshold}
                onChange={(event) => setForm({ ...form, failure_threshold: event.target.value })}
              />
            </div>
          </div>
          <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="discovery-promote">เกณฑ์รับเข้า</label>
              <input
                id="discovery-promote"
                className="input"
                inputMode="numeric"
                placeholder={String(policy?.default_promotion_checks ?? 3)}
                value={form.promotion_checks}
                onChange={(event) => setForm({ ...form, promotion_checks: event.target.value })}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="discovery-window">หน้าต่าง drain (วินาที)</label>
              <input
                id="discovery-window"
                className="input"
                inputMode="numeric"
                placeholder={String(policy?.default_drain_window_seconds ?? 60)}
                value={form.drain_window_seconds}
                onChange={(event) => setForm({ ...form, drain_window_seconds: event.target.value })}
              />
            </div>
          </div>
          <button
            className="btn btn-primary"
            disabled={register.isPending || !form.name.trim() || !form.endpoint.trim()}
            onClick={() => register.mutate()}
          >
            {register.isPending ? 'กำลังลงทะเบียน…' : 'ลงทะเบียนเป้าหมาย'}
          </button>
          {register.isError ? (
            <div className="form-error">{errorText(register.error, 'ลงทะเบียนไม่สำเร็จ')}</div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">สลับเป้าหมายแบบไม่มีช่วงว่าง</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            ระบบจะยอมให้สลับเมื่อเป้าหมายใหม่ผ่านการตรวจต่อเนื่องครบตามเกณฑ์และถูกเพิ่มเข้าตารางเส้นทางแล้วเท่านั้น ·
            เป้าหมายเดิมจะเข้าสู่สถานะ drain และยังมีน้ำหนักอยู่ตลอดช่วง overlap
          </p>
          <div className="field">
            <label htmlFor="discovery-from">เป้าหมายเดิม</label>
            <select
              id="discovery-from"
              className="select"
              value={switchForm.from}
              onChange={(event) => setSwitchForm({ ...switchForm, from: event.target.value })}
            >
              <option value="">เลือกเป้าหมายที่พร้อมใช้งาน</option>
              {drainable.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.name} · {target.percent}%
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="discovery-to">เป้าหมายใหม่</label>
            <select
              id="discovery-to"
              className="select"
              value={switchForm.to}
              onChange={(event) => setSwitchForm({ ...switchForm, to: event.target.value })}
            >
              <option value="">เลือกเป้าหมายที่ผ่านเกณฑ์แล้ว</option>
              {ready
                .filter((target) => target.id !== switchForm.from)
                .map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.name} · ผ่านติดกัน {target.consecutive_successes}/{target.promotion_checks}
                  </option>
                ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="discovery-reason">เหตุผล</label>
            <input
              id="discovery-reason"
              className="input"
              placeholder="โปรโมต canary"
              value={switchForm.reason}
              onChange={(event) => setSwitchForm({ ...switchForm, reason: event.target.value })}
            />
          </div>
          <button
            className="btn"
            disabled={switchTarget.isPending || !switchForm.from || !switchForm.to}
            onClick={() => switchTarget.mutate()}
          >
            {switchTarget.isPending ? 'กำลังสลับ…' : 'ตรวจสอบและสลับ'}
          </button>
          {switchTarget.isError ? (
            <div className="form-error">{errorText(switchTarget.error, 'สลับเป้าหมายไม่สำเร็จ')}</div>
          ) : null}
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="card-label">ตารางเส้นทางถ่วงน้ำหนัก</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            คำนวณจากน้ำหนักจริง · เป้าหมายที่ drain อยู่ยังคงน้ำหนักเดิม
          </span>
        </div>
        <div className="table-wrap">
          {!routing || routing.entries.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty
                title="ตารางเส้นทางว่าง"
                body="ยังไม่มีเป้าหมายที่ผ่านการตรวจและอยู่ในสถานะพร้อมใช้งาน · ตรวจเป้าหมายให้ผ่านเกณฑ์ก่อน"
              />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th>เป้าหมาย</th>
                  <th style={{ width: 130 }}>สถานะ</th>
                  <th style={{ width: 220 }}>สัดส่วนน้ำหนัก</th>
                  <th style={{ width: 110, textAlign: 'right' }}>น้ำหนัก</th>
                  <th style={{ width: 150 }}>กำหนด drain</th>
                </tr>
              </thead>
              <tbody>
                {routing.entries.map((entry) => (
                  <tr key={entry.target_id} style={{ cursor: 'default' }}>
                    <td>
                      <div style={{ fontSize: 12.5 }}>{entry.name}</div>
                      <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }}>{entry.endpoint}</div>
                    </td>
                    <td>
                      <span className={`sev ${STATE_TONE[entry.state]}`}>
                        <i className="sev-dot" />
                        {STATE_LABEL[entry.state]}
                      </span>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 9 }}>
                        <span className="bar" style={{ flex: 1 }}>
                          <i
                            style={{
                              width: `${entry.percent}%`,
                              background: entry.draining ? 'var(--color-amber)' : 'var(--color-gold)',
                            }}
                          />
                        </span>
                        <span className="tnum" style={{ minWidth: 34, textAlign: 'right', fontSize: 12 }}>
                          {entry.percent}%
                        </span>
                      </div>
                    </td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>
                      {entry.effective_weight}
                    </td>
                    <td className="dim" style={{ fontSize: 11 }}>
                      {entry.drain_deadline
                        ? `${thaiDateTime(entry.drain_deadline)} · เหลือ ${secondsLabel(entry.drain_remaining_seconds)}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <span className="chip">น้ำหนักรวม {number(routing?.total_weight ?? 0)}</span>
          <span className="chip">ถูกตัดออก {number(routing?.excluded ?? 0)}</span>
          <span className="chip">สร้างเมื่อ {routing ? thaiDateTime(routing.generated_at) : '—'}</span>
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="card-label">ผลการตรวจล่าสุด</div>
        <div className="table-wrap" style={{ marginTop: 12 }}>
          {!snapshot || snapshot.checks.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty
                title="ยังไม่มีผลการตรวจ"
                body="กด “ตรวจเดี๋ยวนี้” ที่เป้าหมายใดก็ได้ · ระบบจะเรียก HTTP จริงและบันทึกผลไว้ที่นี่"
              />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 180 }}>เวลา</th>
                  <th>เป้าหมาย</th>
                  <th style={{ width: 110 }}>ผล</th>
                  <th style={{ width: 90, textAlign: 'right' }}>HTTP</th>
                  <th style={{ width: 100, textAlign: 'right' }}>เวลา (ms)</th>
                  <th style={{ width: 130 }}>สถานะหลังตรวจ</th>
                  <th>ข้อผิดพลาด</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.checks.map((check) => (
                  <tr key={check.id} style={{ cursor: 'default' }}>
                    <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(check.checked_at)}</td>
                    <td style={{ fontSize: 12 }}>{check.target_name ?? check.target_id.slice(0, 8)}</td>
                    <td>
                      <span className={`sev ${OUTCOME_TONE[check.outcome]}`}>
                        <i className="sev-dot" />
                        {OUTCOME_LABEL[check.outcome]}
                      </span>
                    </td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>
                      {check.status_code > 0 ? check.status_code : '—'}
                    </td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{check.latency_ms}</td>
                    <td className="dim" style={{ fontSize: 11 }}>{STATE_LABEL[check.state_after]}</td>
                    <td className="dim" style={{ fontSize: 11 }}>
                      {check.error || '—'}
                      <div style={{ fontSize: 10.5, marginTop: 2 }}>
                        {check.source === 'manual' ? 'สั่งด้วยมือ' : check.source === 'switch' ? 'ประกอบการสลับ' : 'ตัวควบคุม'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card card-pad">
        <div className="card-label">วิธีที่ตัวควบคุมทำงาน</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>เป้าหมายใหม่เริ่มต้นนอกเส้นทาง · จะเข้าตารางเส้นทางเมื่อผ่านการตรวจต่อเนื่องครบตามเกณฑ์ที่ตั้งไว้</li>
          <li>เป้าหมายที่พร้อมใช้งานจะถูกตัดออกเมื่อล้มเหลวต่อเนื่องครบเกณฑ์ · ระหว่างนั้นน้ำหนักยังคงเดิม</li>
          <li>การตรวจคือ HTTP GET จริงพร้อมเวลารอ {policy?.probe_timeout_ms ?? 2000} ms · ไม่ตาม redirect และไม่ใช้ proxy จาก environment</li>
          <li>ผลตอบกลับ 2xx เท่านั้นที่นับว่าผ่าน · 2xx ที่ช้ากว่า {policy?.slow_threshold_ms ?? 400} ms ถูกบันทึกเป็น “ผ่านแต่ช้า” และยังนับสะสม</li>
          <li>เริ่ม drain แล้วน้ำหนักยังอยู่ครบ · เมื่อจบ drain น้ำหนักจึงเป็นศูนย์และเป้าหมายถูกถอดออกถาวร</li>
          <li>การสลับจะทำได้เมื่อเป้าหมายใหม่ผ่านเกณฑ์แล้วเท่านั้น · ช่วง overlap คือหน้าต่าง drain ของเป้าหมายเดิม</li>
          <li>ทุกตารางในส่วนนี้บังคับ RLS ตามองค์กร · และส่วนนี้ไม่เขียน platform.audit_events</li>
        </ul>
      </div>
    </>
  )
}
